import { type GetApiTracksQueryParams } from '@/shared/api'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  trackCreate,
  trackDelete,
  trackMultipleDelete,
  trackUpdate,
} from '../api/track.api'
import {
  type TracksQueryModelWithPagination,
  type TracksSortState,
  createTracksQueryModel,
} from '../lib/tracks-query-model'
import { selectedTracksDeleted } from './track.model'

interface TracksTableGateProps {
  initialFilters?: GetApiTracksQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: TracksSortState
}
export const TracksTableGate = createGate<TracksTableGateProps>()

export const TracksTableTracksQueryModel = createTracksQueryModel({
  usePagination: true,
  accumulate: false,
}) as TracksQueryModelWithPagination
const { availableSet, unavailableSet } = TracksTableTracksQueryModel
export const {
  //   Query
  query: tracksTableQuery,
  //   Init
  $filters: $tracksTableFilters,
  filtersInit: tracksTableFiltersInit,
  filtersChanged: tracksTableFiltersChanged,
  filtersReset: tracksTableFiltersReset,
  //   Search
  $search: $tracksTableSearch,
  searchChanged: tracksTableSearchChanged,
  searchReset: tracksTableSearchReset,
  //   Sort
  $sort: $tracksTableSort,
  sortChanged: tracksTableSortChanged,
  sortReset: tracksTableSortReset,
  //   Pagination
  $page: $tracksTablePage,
  pageChanged: tracksTablePageChanged,
  pageReset: tracksTablePageReset,
  $pageSize: $tracksTablePageSize,
  pageSizeChanged: tracksTablePageSizeChanged,
  //   Refresh
  refreshed: tracksTableRefreshed,
} = TracksTableTracksQueryModel

// Search input

export const $tracksTableSearchInputValue = createStore('')
  .on(tracksTableSearchChanged, (_, q) => q)
  .reset(tracksTableSearchReset)

// Table Row Selection

export const tracksTableSelectedRowKeysChanged = createEvent<React.Key[]>()
export const $tracksTableSelectedRowKeys = createStore<React.Key[]>([])
  .on(tracksTableSelectedRowKeysChanged, (_, payload) => payload)
  .reset(tracksTableQuery.finished.finally)
export const $areTracksTableRowsSelected = $tracksTableSelectedRowKeys.map(
  (keys) => keys.length > 0,
)
export const selectedTracksDeleteRequested = createEvent()

sample({
  source: $tracksTableSelectedRowKeys,
  clock: selectedTracksDeleteRequested,
  fn: (keys) => ({ trackIds: keys.map(String) }),
  target: selectedTracksDeleted,
})

sample({
  clock: TracksTableGate.open,
  target: availableSet,
})

// Setup initial params
// Page
sample({
  clock: TracksTableGate.open,
  source: combine(
    TracksTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: tracksTablePageChanged,
})
// Page Size
sample({
  clock: TracksTableGate.open,
  source: combine(
    TracksTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: tracksTablePageSizeChanged,
})
// Search
sample({
  clock: TracksTableGate.open,
  source: combine(
    TracksTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: tracksTableSearchChanged,
})
// Sort
sample({
  clock: TracksTableGate.open,
  source: combine(
    TracksTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: tracksTableSortChanged,
})
// Filters
sample({
  source: TracksTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: tracksTableFiltersInit,
})

sample({
  clock: TracksTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $tracksTablePage,
    queryData: tracksTableQuery.$data,
  }),
  clock: [trackDelete.finished.success, trackMultipleDelete.finished.success],
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: tracksTablePageChanged,
})

sample({
  clock: [
    trackCreate.finished.success,
    trackUpdate.finished.success,
    trackDelete.finished.success,
    trackMultipleDelete.finished.success,
  ],
  target: tracksTableRefreshed,
})
